import { createStorage } from '@matix/utils';

/**
 * The notes this terminal has actually typed, most recent first.
 *
 * A till is not a QR menu: the customer at the counter says "no onions" and the
 * cashier has one hand on the screen and a queue behind them. Typing the same
 * six phrases all day is the slow part, so every note that gets saved is
 * remembered here and offered back as a one-tap chip.
 *
 * Deliberately NOT tenant-configurable: what a kitchen actually gets asked for
 * differs per branch, per season and per cashier, and nobody would maintain a
 * list in admin. This one maintains itself.
 *
 * Kept per terminal (localStorage, no expiry) because the phrases are stable —
 * unlike an address, a stale "extra spicy" costs nothing.
 */

const STORE = createStorage('pos:cashier');
const KEY = 'recent_line_notes';
const MAX = 8;
/** Longer than a chip can usefully show, and the column is nvarchar(500). */
const MAX_LEN = 500;

export function readRecentLineNotes(): string[] {
  const raw = STORE.get<unknown>(KEY);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((n): n is string => typeof n === 'string' && n.trim() !== '')
    .map((n) => n.trim().slice(0, MAX_LEN))
    .slice(0, MAX);
}

/**
 * Move `note` to the front of the list. Matching is case-insensitive so
 * "No Onions" typed today does not sit next to "no onions" from yesterday —
 * the newest spelling wins and the old one drops out.
 */
export function rememberLineNote(note: string): void {
  const clean = note.trim().slice(0, MAX_LEN);
  if (clean === '') return;
  const rest = readRecentLineNotes().filter(
    (n) => n.toLocaleLowerCase() !== clean.toLocaleLowerCase(),
  );
  STORE.set(KEY, [clean, ...rest].slice(0, MAX));
}
